import { useState, useEffect, useCallback, useRef } from 'react';
import { connectSocket, disconnectSocket, onNotification, onNotificationRead, onAllNotificationsRead, removeNotificationListener, getSocket, waitForSocketConnection } from '../services/socketService';
import * as notificationApi from '../api/notificationApi';

export const useNotifications = () => {
    const [notifications, setNotifications] = useState([]);
    const [unreadCount, setUnreadCount] = useState(0);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [shouldOpenDropdown, setShouldOpenDropdown] = useState(false);
    const socketRef = useRef(null);
    const callbacksRef = useRef({});

    // Play notification sound (simple and clean)
    const playNotificationSound = useCallback(() => {
        // Only play sound if AudioContext is in allowed state
        try {
            const AudioContext = window.AudioContext || window.webkitAudioContext;
            if (!AudioContext) return;
            
            const audioContext = new AudioContext();
            
            // Check if context is allowed to start
            if (audioContext.state === 'suspended') {
                audioContext.close();
                return;
            }
            
            const oscillator = audioContext.createOscillator();
            const gainNode = audioContext.createGain();

            oscillator.connect(gainNode);
            gainNode.connect(audioContext.destination);

            oscillator.frequency.value = 800;
            oscillator.type = 'sine';
            
            gainNode.gain.setValueAtTime(0, audioContext.currentTime);
            gainNode.gain.linearRampToValueAtTime(0.3, audioContext.currentTime + 0.01);
            gainNode.gain.exponentialRampToValueAtTime(0.001, audioContext.currentTime + 0.15);

            oscillator.start(audioContext.currentTime);
            oscillator.stop(audioContext.currentTime + 0.15);
            
            setTimeout(() => {
                audioContext.close();
            }, 200);
        } catch (error) {
            // Silently fail if audio not supported or not allowed
        }
    }, []);

    // Fetch initial notifications - memoized to prevent unnecessary re-renders
    const fetchNotifications = useCallback(async () => {
        try {
            setLoading(true);
            const [notifResponse, countResponse] = await Promise.all([
                notificationApi.getUserNotifications(1, 50),
                notificationApi.getUnreadCount()
            ]);

            if (notifResponse.status === 200) {
                setNotifications(notifResponse.data.notifications || []);
            }

            if (countResponse.status === 200) {
                setUnreadCount(countResponse.data.count || 0);
            }

            setError(null);
        } catch (err) {
            setError('Failed to load notifications');
        } finally {
            setLoading(false);
        }
    }, []);

    // Mark notification as read
    const markAsRead = useCallback(async (notificationId) => {
        try {
            await notificationApi.markAsRead(notificationId);
            
            setNotifications(prev => prev.map(n => 
                n._id === notificationId ? { ...n, isRead: true } : n
            ));
            
            // Update unread count - sync with server to ensure accuracy
            setUnreadCount(prev => Math.max(0, prev - 1));
            
            // Optionally refetch count from server for accuracy
            try {
                const countResponse = await notificationApi.getUnreadCount();
                if (countResponse.status === 200) {
                    setUnreadCount(countResponse.data.count || 0);
                }
            } catch (err) {
                // Silent error handling - fallback to local decrement
            }
        } catch (err) {
            // Silent error handling
        }
    }, []);

    // Mark all as read
    const markAllAsRead = useCallback(async () => {
        try {
            await notificationApi.markAllAsRead();
            
            setNotifications(prev => prev.map(n => ({ ...n, isRead: true })));
            setUnreadCount(0);
        } catch (err) {
            // Silent error handling
        }
    }, []);

    // Delete notification
    const deleteNotification = useCallback(async (notificationId) => {
        try {
            await notificationApi.deleteNotification(notificationId);
            
            setNotifications(prev => {
                const deletedNotif = prev.find(n => n._id === notificationId);
                if (deletedNotif && !deletedNotif.isRead) {
                    setUnreadCount(count => Math.max(0, count - 1));
                }
                return prev.filter(n => n._id !== notificationId);
            });
        } catch (err) {
            // Silent error handling
        }
    }, []);

    // Clear all notifications
    const clearAll = useCallback(async () => {
        try {
            await notificationApi.clearAllNotifications();
            
            setNotifications([]);
            setUnreadCount(0);
        } catch (err) {
            // Silent error handling
        }
    }, []);

    // Initialize socket connection and fetch notifications
    useEffect(() => {
        let isMounted = true;
        let connectHandler = null;

        // Connect socket
        const socket = connectSocket();
        socketRef.current = socket;

        // Fetch initial notifications
        fetchNotifications();

        // Set up socket listeners - wait for connection if needed
        const setupListeners = () => {
            if (!socket || !isMounted) return;

            // Clean up any existing listeners first
            if (callbacksRef.current.notification) {
                removeNotificationListener(
                    callbacksRef.current.notification,
                    callbacksRef.current.notificationRead,
                    callbacksRef.current.allNotificationsRead
                );
            }

            // Listen for new notifications
            const notificationCallback = (notification) => {
                if (!isMounted) return;
                
                // Add to notifications list (avoid duplicates)
                setNotifications(prev => {
                    // Check if notification already exists
                    const exists = prev.some(n => n._id === notification._id);
                    if (exists) return prev;
                    
                    return [notification, ...prev];
                });
                
                // Increment unread count
                setUnreadCount(prev => prev + 1);
                
                // Trigger dropdown to open
                setShouldOpenDropdown(true);
                
                // Play sound
                try {
                    playNotificationSound();
                } catch (error) {
                    // Silent error handling
                }
                
                // Show browser notification if permitted
                if ('Notification' in window && Notification.permission === 'granted') {
                    try {
                        new Notification(notification.title, {
                            body: notification.message,
                            icon: '/favicon.ico',
                            badge: '/favicon.ico'
                        });
                    } catch (err) {
                        // Silent error handling for browser notifications
                    }
                }
            };
            callbacksRef.current.notification = notificationCallback;
            onNotification(notificationCallback);

            // Listen for notification read events
            const notificationReadCallback = (data) => {
                if (!isMounted) return;
                setNotifications(prev => prev.map(n => 
                    n._id === data.notificationId ? { ...n, isRead: true } : n
                ));
            };
            callbacksRef.current.notificationRead = notificationReadCallback;
            onNotificationRead(notificationReadCallback);

            // Listen for all notifications read
            const allNotificationsReadCallback = () => {
                if (!isMounted) return;
                setNotifications(prev => prev.map(n => ({ ...n, isRead: true })));
                setUnreadCount(0);
            };
            callbacksRef.current.allNotificationsRead = allNotificationsReadCallback;
            onAllNotificationsRead(allNotificationsReadCallback);
        };

        // Wait for socket connection before setting up listeners
        if (socket) {
            // If already connected, set up immediately
            // Otherwise, waitForSocketConnection will set up when connected
            if (socket.connected) {
                setupListeners();
            } else {
                // Store the handler so we can clean it up if needed
                connectHandler = () => {
                    if (isMounted) {
                        setupListeners();
                    }
                };
                socket.once('connect', connectHandler);
            }
        }

        // Request browser notification permission
        if ('Notification' in window && Notification.permission === 'default') {
            Notification.requestPermission().catch(() => {
                // Silent error handling
            });
        }

        // Cleanup
        return () => {
            isMounted = false;
            
            // Remove connect handler if it was set
            if (socket && connectHandler) {
                socket.off('connect', connectHandler);
            }
            
            // Remove specific callbacks
            if (callbacksRef.current.notification) {
                removeNotificationListener(
                    callbacksRef.current.notification,
                    callbacksRef.current.notificationRead,
                    callbacksRef.current.allNotificationsRead
                );
                callbacksRef.current = {};
            }
            // Don't disconnect socket as it might be used elsewhere
            // Only disconnect if we're sure no other components need it
        };
    }, [fetchNotifications, playNotificationSound]);

    return {
        notifications,
        unreadCount,
        loading,
        error,
        shouldOpenDropdown,
        setShouldOpenDropdown,
        markAsRead,
        markAllAsRead,
        deleteNotification,
        clearAll,
        refreshNotifications: fetchNotifications
    };
};

